import { create } from 'zustand'
import type {
  LoadState,
  MatrixView,
  Ministry,
  Person,
  Plan,
  Position,
  ServiceType,
  Song,
  Team,
  TeamMembership,
} from '@/types'
import { api, describeError } from '@/services/api'

interface DataState {
  status: LoadState
  error: string | null

  ministries: Ministry[]
  teams: Team[]
  positions: Position[]
  people: Person[]
  memberships: TeamMembership[]
  serviceTypes: ServiceType[]
  songs: Song[]
  plans: Plan[]
  matrix: MatrixView | null
  /** The window the grid is currently showing, so it can be reloaded. */
  matrixRange: { from?: string; to?: string } | null
  /**
   * Why the grid is not here, when it is not here.
   *
   * `matrix === null` alone cannot tell "has not loaded yet" from "the
   * request failed", so the screen showed a skeleton forever when /matrix
   * 500'd — see mymdv-home-cache: three states, never two.
   */
  matrixError: string | null

  /** Loads everything the shell needs. Safe to call repeatedly. */
  loadCore: () => Promise<void>
  refreshSongs: () => Promise<void>
  refreshPlans: () => Promise<void>
  loadMatrix: (range?: { from?: string; to?: string }) => Promise<void>
  /** One more slot on a plan the matrix is showing, then reload the grid. */
  addMatrixSlot: (planId: string, positionId: string) => Promise<void>
  /** A slot with somebody already in it — the matrix's own assign popup. */
  addMatrixPerson: (planId: string, positionId: string, personId: string) => Promise<void>
  /** A song onto a plan's running order, from the matrix's songs row. */
  addMatrixSong: (planId: string, songId: string, title: string, arrangementId?: string, durationSeconds?: number) => Promise<void>

  /** Local cache patches so the UI updates without a full refetch. */
  upsertSong: (song: Song) => void
  removeSong: (songId: string) => void
  upsertPlan: (plan: Plan) => void
  removePlan: (planId: string) => void

  personById: (id: string | null | undefined) => Person | undefined
  positionById: (id: string) => Position | undefined
  teamById: (id: string) => Team | undefined
  songById: (id: string | null | undefined) => Song | undefined
}

export const useDataStore = create<DataState>((set, get) => ({
  status: 'idle',
  error: null,
  ministries: [],
  teams: [],
  positions: [],
  people: [],
  memberships: [],
  serviceTypes: [],
  songs: [],
  plans: [],
  matrix: null,
  matrixRange: null,
  matrixError: null,

  async loadCore() {
    if (get().status === 'loading') return
    set({ status: 'loading', error: null })
    try {
      const [ministries, teams, positions, people, memberships, serviceTypes, songs, plans] =
        await Promise.all([
          api.get<Ministry[]>('/ministries'),
          api.get<Team[]>('/teams'),
          api.get<Position[]>('/positions'),
          api.get<Person[]>('/people'),
          api.get<TeamMembership[]>('/memberships'),
          api.get<ServiceType[]>('/service-types'),
          api.get<Song[]>('/songs'),
          api.get<Plan[]>('/plans'),
        ])
      set({
        ministries, teams, positions, people, memberships, serviceTypes, songs, plans,
        status: 'success',
      })
    } catch (error) {
      set({ status: 'error', error: describeError(error) })
    }
  },

  async refreshSongs() {
    try {
      set({ songs: await api.get<Song[]>('/songs') })
    } catch (error) {
      set({ error: describeError(error) })
    }
  },

  async refreshPlans() {
    try {
      set({ plans: await api.get<Plan[]>('/plans') })
    } catch (error) {
      set({ error: describeError(error) })
    }
  },

  async loadMatrix(range) {
    // Remembered so a change made FROM the grid can reload the same window
    // rather than silently widening or narrowing it.
    set({ matrixRange: range ?? null })
    try {
      const params = new URLSearchParams()
      if (range?.from) params.set('from', range.from)
      if (range?.to) params.set('to', range.to)
      const suffix = params.toString() ? `?${params.toString()}` : ''
      set({ matrix: await api.get<MatrixView>(`/matrix${suffix}`), matrixError: null })
    } catch (error) {
      /* Both: `error` drives the page's banner, `matrixError` is what lets
         the grid's own slot say something instead of pretending to load. */
      set({ matrixError: describeError(error), error: describeError(error) })
    }
  },

  /*
   * The matrix is a read-only overview of many weeks, with one exception:
   * adding a slot. Ian, 2026-10-09 — scanning several Sundays at once is
   * exactly when you notice a week needs an extra pair of hands, and making
   * that a trip into the plan and back loses your place in the grid.
   *
   * It reloads the whole matrix rather than patching one cell: a cell is an
   * aggregate (counts by status, plus the people in it) and recomputing that
   * by hand here would be a second implementation of the server's sums, free
   * to drift from it.
   */
  async addMatrixSlot(planId, positionId) {
    try {
      await api.post('/assignments', { planId, positionId })
      await get().loadMatrix(get().matrixRange ?? undefined)
    } catch (error) {
      set({ error: describeError(error) })
    }
  },

  /*
   * Both of these are addMatrixSlot's shape: post, then reload the whole
   * grid. A cell is an aggregate — counts by status, the people in it, the
   * open slots — so patching one locally means recomputing all of that in two
   * places, and the second copy is the one that drifts.
   *
   * `status` is deliberately not sent: POST /assignments decides it from the
   * plan, so a stale client cannot leave a Sunday pending that should have
   * auto-confirmed.
   */
  async addMatrixPerson(planId, positionId, personId) {
    try {
      await api.post('/assignments', { planId, positionId, personId })
      await get().loadMatrix(get().matrixRange ?? undefined)
    } catch (error) {
      set({ error: describeError(error) })
    }
  },

  async addMatrixSong(planId, songId, title, arrangementId, durationSeconds) {
    try {
      /* The same item OrderTable builds from a picked song, so a song added
         from the matrix is indistinguishable from one added in the plan. */
      await api.post(`/plans/${planId}/items`, {
        type: 'song',
        title,
        songId,
        arrangementId,
        durationSeconds: durationSeconds || 300,
      })
      await get().loadMatrix(get().matrixRange ?? undefined)
    } catch (error) {
      set({ error: describeError(error) })
    }
  },

  upsertSong(song) {
    const songs = get().songs
    const index = songs.findIndex((s) => s.id === song.id)
    const next = index >= 0 ? songs.map((s) => (s.id === song.id ? song : s)) : [...songs, song]
    set({ songs: next.sort((a, b) => a.title.localeCompare(b.title)) })
  },

  removeSong(songId) {
    set({ songs: get().songs.filter((s) => s.id !== songId) })
  },

  upsertPlan(plan) {
    const plans = get().plans
    const index = plans.findIndex((p) => p.id === plan.id)
    const next = index >= 0 ? plans.map((p) => (p.id === plan.id ? plan : p)) : [...plans, plan]
    set({
      plans: next.sort((a, b) =>
        `${a.serviceDate}${a.startTime}`.localeCompare(`${b.serviceDate}${b.startTime}`),
      ),
    })
  },

  removePlan(planId) {
    set({ plans: get().plans.filter((p) => p.id !== planId) })
  },

  personById: (id) => (id ? get().people.find((p) => p.id === id) : undefined),
  positionById: (id) => get().positions.find((p) => p.id === id),
  teamById: (id) => get().teams.find((t) => t.id === id),
  songById: (id) => (id ? get().songs.find((s) => s.id === id) : undefined),
}))
